// MAP AUDIO (owner 2026-11-02: 'give each map its own ambient sound … but also don't forget the music'). Data types for the
// per-map soundscape + generative score. Each map folder may export `AUDIO: MapAudioDef` from src/maps/<id>/audio.ts (found
// by src/maps/audioRegistry.ts). Pure data: no WebAudio objects, no three, no DOM, so recipes can be validated in unit tests.
// Everything is original procedural synthesis (src/audio/ambience.ts, src/audio/score.ts); no samples, no melodies from
// anywhere else: keys / modes / patterns below are plain music theory.

// ------------------------------------------------------------------------------------------------------------ ambience
export type NoiseColor = 'white' | 'pink' | 'brown';

/** A continuous layer (wind, hum, sand hiss…). Gain follows day/night, the storm level (fog hazards) and random gusts. */
export interface BedSpec {
  /** a looping stereo noise colour, or a few steady oscillators (hums, drones) */
  src: NoiseColor | { wave: OscillatorType; freqs: number[] };
  filter: BiquadFilterType; freq: number; q?: number;
  /** linear level before the map's ambience `level` */
  gain: number;
  /** level multipliers by day / at full night (default 1; the live cycle crossfades between them) */
  day?: number; night?: number;
  /** extra level added at full storm (global fog hazards: sandstorm, blizzard, dust storm) */
  storm?: number;
  /** slow random swells: level x (1 ± depth), filter x sweep^(±1), a new target every `every` seconds */
  gust?: { depth: number; every: [number, number]; sweep?: number };
  /** noise playback rate (below 1 = darker / slower texture) */
  rate?: number;
}

/** Sparse one-shot critters / events, played on a small pool of persistent voices. */
export type EventKind =
  | 'bird' | 'songbird' | 'cricket' | 'owl' | 'raptor' | 'coo' | 'flap'        // fauna (plains, canyon, city, isles)
  | 'howl' | 'insect' | 'call' | 'croak' | 'whale'                             // beasts, alien life, sky giants
  | 'siren' | 'creak' | 'spark' | 'pass'                                       // city
  | 'chime' | 'icezing' | 'drip' | 'bubble'                                    // crystal, ice, water, lava
  | 'rumble' | 'thunder' | 'hiss' | 'crack' | 'rockfall' | 'gust';             // ground + weather

export interface EventSpec {
  kind: EventKind;
  /** seconds between events at full weight (random in range); lower weight = rarer and a little quieter */
  every: [number, number];
  gain: number;
  /** weight by day / at full night (default 1); 0 = never at that time */
  day?: number; night?: number;
  /** extra weight at full storm */
  storm?: number;
  /** pitch multiplier (default 1) */
  pitch?: number;
  /** stereo spread 0..1 (default 0.8) */
  spread?: number;
  /** echo send 0..1 (default 0.15) */
  echo?: number;
  /** pool size = the cap on overlapping events of this kind (default 1, max 3) */
  voices?: number;
}

export interface EchoSpec { l: number; r: number; fb: number; damp: number; wet: number }

export interface AmbienceSpec {
  /** overall ambience level, calibrated with scripts/map-audio-check.mjs so every map sits near -40 dB (150 Hz-weighted) */
  level: number;
  beds: BedSpec[];
  events: EventSpec[];
  /** shared space for one-shots: a stereo feedback delay (canyon echo, city reflections) */
  echo: EchoSpec;
}

// ------------------------------------------------------------------------------------------------------------ music
/** A monophonic synth voice (bass, arp, lead). Persistent oscillators, re-triggered envelopes: no nodes per note. */
export interface InstSpec {
  wave: OscillatorType;
  /** second oscillator detuned by this many cents (thicker) */
  detune?: number;
  /** FM bell / pluck colour: modulator at ratio x note, index = peak depth in multiples of the note frequency */
  fm?: { ratio: number; index: number; decay: number };
  /** vibrato (ignored when fm is set) */
  vibrato?: { rate: number; cents: number };
  cutoff: number; q?: number;
  /** filter envelope: cutoff x (1 + env) at the attack, back to cutoff over `dec` */
  env?: number;
  atk: number; dec: number; sus: number; rel: number;
  gain: number;
  /** portamento time constant (s) */
  glide?: number;
  /** soft saturation (1 = none … 4 = gritty) */
  drive?: number;
  /** echo send 0..1 */
  send?: number;
  /** octave offset from the root (root is around MIDI 48) */
  octave: number;
  /** note length as a fraction of its slot (default 0.9) */
  legato?: number;
}

/** Sustained chord pad: `voices` x 2 detuned oscillators through one filter; chords glide between bars. */
export interface PadSpec { wave: OscillatorType; voices: 3 | 4; detune: number; cutoff: number; q?: number; gain: number; octave: number; send?: number }
/** Steady drone (root / fifth …) under everything. */
export interface DroneSpec { wave: OscillatorType; notes: number[]; cutoff: number; gain: number; octave: number }

export type KitName = 'tight' | 'taiko' | 'frame' | 'soft';
/** 16- or 12-step patterns: 'X' accent, 'x' normal, 'g' ghost, 'o' open (hat) / long (tom), '.' rest */
export interface DrumPatterns { kick?: string; snare?: string; hat?: string; tom?: string }
export interface DrumSpec {
  kit: KitName; gain: number;
  main: DrumPatterns;
  /** the last bar of every 8 */
  fill?: DrumPatterns;
  /** boss alive / overtime: replaces main */
  hot?: DrumPatterns;
}

export interface Motif {
  /** [scale-degree offset from the chord root (null = rest), length in steps] */
  notes: [number | null, number][];
}

export interface ScoreSpec {
  bpm: number;
  /** 16 = 4/4 in 16ths, 12 = 12/8 */
  steps: 16 | 12;
  /** MIDI note of the tonic (around 48 = C3) */
  root: number;
  /** 7 semitone offsets */
  mode: readonly number[];
  /** one chord per bar: scale degrees (0-based) */
  progression: number[];
  /** 0..0.4: delay of every odd step, as a fraction of a step */
  swing?: number;
  pad?: PadSpec;
  drone?: DroneSpec;
  bass: InstSpec & { pattern: string; hot?: string };
  /** chord-tone indices per step (0 root, 1 third, 2 fifth, 3+ next octave), -1 = rest */
  arp?: InstSpec & { pattern: number[]; hot?: number[] };
  lead?: InstSpec & { motifs: Motif[]; /** bars of each 8-bar phrase in which the lead plays */ bars: number[] };
  drums?: DrumSpec;
  /** music echo, in steps */
  echo: { steps: number; fb: number; wet: number; damp: number };
  /** overall music level, calibrated with scripts/map-audio-check.mjs to about the battle theme's loudness (-37 dB weighted) */
  level: number;
}

export interface MapAudioDef {
  id: string;
  ambience: AmbienceSpec;
  music: {
    score: ScoreSpec;
    /** 'sample': play the shared battle_theme.mp3 here by default (the score stays reachable with ?music=gen) */
    prefer?: 'sample' | 'score';
    /** one line on the mood, shown in QA output */
    mood: string;
  };
}

// ------------------------------------------------------------------------------------------------------------ theory
export const MODES = {
  ionian: [0, 2, 4, 5, 7, 9, 11],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  mixolydian: [0, 2, 4, 5, 7, 9, 10],
  aeolian: [0, 2, 3, 5, 7, 8, 10],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
  phrygianDominant: [0, 1, 4, 5, 7, 8, 10],
} as const;

/** MIDI note of scale degree `deg` (any integer; 7 = the tonic an octave up) */
export function degreeNote(root: number, mode: readonly number[], deg: number): number {
  const o = Math.floor(deg / 7), i = deg - o * 7;
  return root + mode[i] + 12 * o;
}
export const midiHz = (m: number): number => 440 * Math.pow(2, (m - 69) / 12);

export const EVENT_KINDS: readonly EventKind[] = ['bird', 'songbird', 'cricket', 'owl', 'raptor', 'coo', 'flap', 'howl', 'insect', 'call', 'croak', 'whale',
  'siren', 'creak', 'spark', 'pass', 'chime', 'icezing', 'drip', 'bubble', 'rumble', 'thunder', 'hiss', 'crack', 'rockfall', 'gust'];

/** Structural checks (unit tests + a dev warning): returns a list of problems, empty when the recipe is sound. */
export function validateMapAudio(d: MapAudioDef): string[] {
  const out: string[] = [], a = d.ambience, s = d.music.score;
  const pat = (name: string, p: string | undefined) => { if (p !== undefined && (p.length !== s.steps || /[^Xxgo.]/.test(p))) out.push(`${name}: pattern '${p}' must be ${s.steps} of X x g o .`); };
  if (!(a.level > 0 && a.level < 4)) out.push('ambience.level out of range');
  if (a.beds.length < 2 || a.beds.length > 5) out.push('ambience: 2..5 beds');
  if (a.events.length < 3 || a.events.length > 9) out.push('ambience: 3..9 event kinds');
  const voices = a.events.reduce((n, e) => n + (e.voices ?? 1), 0);
  if (voices > 12) out.push(`ambience: ${voices} event voices (cap 12)`);
  for (const b of a.beds) {
    if (!(b.gain > 0 && b.freq > 10 && b.freq < 20000)) out.push('bed: bad gain/freq');
    if (typeof b.src !== 'string' && (b.src.freqs.length < 1 || b.src.freqs.length > 4)) out.push('bed: 1..4 oscillators');
  }
  for (const e of a.events) {
    if (!EVENT_KINDS.includes(e.kind)) out.push(`event kind ${e.kind}`);
    if (!(e.every[0] > 0.2 && e.every[1] >= e.every[0])) out.push(`event ${e.kind}: bad 'every'`);
    if ((e.voices ?? 1) > 3) out.push(`event ${e.kind}: max 3 voices`);
    if ((e.day ?? 1) <= 0 && (e.night ?? 1) <= 0 && !e.storm) out.push(`event ${e.kind}: never plays`);
  }
  if (!(s.bpm >= 60 && s.bpm <= 170)) out.push('bpm out of range');
  if (s.mode.length !== 7) out.push('mode must have 7 notes');
  if (s.progression.length < 2 || s.progression.length > 16) out.push('progression: 2..16 chords');
  if (!(s.level > 0 && s.level < 4)) out.push('music.level out of range');
  if (s.bass.pattern.length !== s.steps || /[^1358\-.]/.test(s.bass.pattern)) out.push('bass pattern: steps of 1 3 5 8 - .');
  if (s.bass.hot && (s.bass.hot.length !== s.steps || /[^1358\-.]/.test(s.bass.hot))) out.push('bass hot pattern');
  if (s.arp && s.arp.pattern.length !== s.steps) out.push('arp pattern length');
  if (s.arp?.hot && s.arp.hot.length !== s.steps) out.push('arp hot pattern length');
  if (s.lead) for (const m of s.lead.motifs) { const n = m.notes.reduce((k, x) => k + x[1], 0); if (n > s.steps * 2 || n < 4) out.push(`lead motif spans ${n} steps`); }
  if (s.drums) for (const [k, ps] of Object.entries({ main: s.drums.main, fill: s.drums.fill, hot: s.drums.hot })) if (ps) for (const [v, p] of Object.entries(ps)) pat(`drums.${k}.${v}`, p);
  return out;
}
